import React from "react";
import { Tooltip, IconButton, Pagination } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StatusChip from "./Status";
import { isEmpty, isNull, isUndefined } from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import useLobby from "@/hooks/use-lobby";
import QuotationDetailsModal from "@/pages/buyer/home/components/lobby/responsiveSeller/Wedgits/QuotationDetailsModal";

interface OfferQuatationslist {
  offerID?: string;
  offerStatus?: string;
  updateNew: () => void;
}

interface Product {
  productId: string;
  unitName: string;
  SKUCode: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  attachment?: File | null;
  image?: { url: string } | null | undefined;
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  status: string;
  products: Product[];
  company: {
    CompanyNr: string;
    nameAr: string;
  };
}

const ListofQuatations: React.FC<OfferQuatationslist> = ({
  offerID,
  offerStatus,
  updateNew,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const {
    getListtenderQuotationsByOfferId,
    getListtenderQuotationsByOfferIdMy,
  } = useLobby();
  const [listOffers, setListOffers] = React.useState<TenderQuotation[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 7;
  const [totalPages, setTotalPages] = React.useState(1);
  const [selectedQuotation, setSelectedQuotation] =
    React.useState<TenderQuotation | null>(null);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  React.useEffect(() => {
    const fetchQuotations = async () => {
      if (offerID) {
        try {
          if (userRole === ROLE.BUYER) {
            const Total = await getListtenderQuotationsByOfferId(offerID);
            if (Total) {
              setListOffers(Total.data);
              setTotalPages(Math.ceil(Total.data.length / itemsPerPage));
            } else {
              setListOffers([]);
            }
          } else {
            const Total = await getListtenderQuotationsByOfferIdMy(offerID);
            if (Total) {
              setListOffers(Total.data);
              setTotalPages(Math.ceil(Total.data.length / itemsPerPage));
            } else {
              setListOffers([]);
            }
          }
        } catch (error) {
          console.error("Error fetching Offers:", error);
        }
      }
    };
    fetchQuotations();
  }, [offerID, currentPage]);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOffers = listOffers.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleViewDetails = (quotation: TenderQuotation) => {
    setSelectedQuotation(quotation);
  };

  const handleCloseDetailsModal = async () => {
    setSelectedQuotation(null);
    if (offerID) {
      updateNew();
      const Total = await getListtenderQuotationsByOfferId(offerID);
      if (Total) {
        setListOffers(Total.data);
        setTotalPages(Math.ceil(Total.data.length / itemsPerPage));
      } else {
        setListOffers([]);
      }
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  return (
    <div
      style={{
        direction: locale === LANG.EN ? "ltr" : "rtl",
        // padding: "20px",
        backgroundColor: "#fff",
        borderRadius: "12px",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          fontWeight: "bold",
          marginBottom: "20px",
          color: "#644980",
        }}
      >
        {t("navigation.Quotations")}
      </h2>
      {!isEmpty(paginatedOffers) ? (
        <div className="overflow-scroll no-scrollbar">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 1fr 1fr 50px",
              padding: "10px",
              backgroundColor: "#eaeaea",
              fontWeight: "bold",
              borderRadius: "8px",
              textAlign: locale === LANG.EN ? "left" : "right",
            }}
          >
            <div className="min-w-44 md:min-w-32 xl:min-w-64">
              {t("navigation.Opportunity")}
            </div>
            <div className="min-w-44 md:min-w-32 xl:min-w-64">
              {t("checkout.fields.company")}
            </div>
            <div className="min-w-44 md:min-w-32 xl:min-w-64">
              {t("tender.value")}
            </div>
            <div className="min-w-44 md:min-w-32 xl:min-w-64">
              {t("orders.fields.status")}
            </div>
            <div className="min-w-44 md:min-w-32 xl:min-w-64"></div>
          </div>
          {paginatedOffers.map((item, index) => (
            <div
              key={index}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr 1fr 1fr 50px",
                padding: "10px",
                backgroundColor: index % 2 === 0 ? "#fff" : "#f4f4f4",
                alignItems: "center",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#e0f7fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  index % 2 === 0 ? "#fff" : "#f4f4f4")
              }
            >
              <div className="min-w-44 md:min-w-32 xl:min-w-64">
                {isNull(item.tender) ? "" : item.tender.title}
              </div>
              <div className="min-w-44 md:min-w-32 xl:min-w-64">
                {item.company.CompanyNr}
              </div>
              <div className="min-w-44 md:min-w-32 xl:min-w-64">
                {item.products
                  .reduce(
                    (acc: number, product: Product) =>
                      acc +
                      (product.unitPrice ?? 0) *
                        (product.quantity ?? 0) *
                        (1 + (product.vat ?? 0) / 100),
                    0
                  )
                  .toFixed(2)}
              </div>
              <div className="min-w-44 md:min-w-32 xl:min-w-64">
                <StatusChip
                  status={isNull(item.status) ? "Opened" : item.status}
                />
              </div>
              <div className="min-w-44 md:min-w-32 xl:min-w-64">
                <Tooltip title={t("general.view")}>
                  <IconButton onClick={() => handleViewDetails(item)}>
                    <VisibilityOutlinedIcon style={{ color: "#644980" }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": {
                  "&.Mui-selected": {
                    backgroundColor: "#644980",
                    color: "#fff",
                  },
                  "&:hover": {
                    backgroundColor: "#b392c9",
                    color: "#fff",
                  },
                },
              }}
            />
          </div>
        </div>
      ) : (
        <p style={{ textAlign: "center", color: "#644980" }}>
          {t("offers.noAvailable")}
        </p>
      )}
      {selectedQuotation && (
        <QuotationDetailsModal
          open={!!selectedQuotation}
          onClose={handleCloseDetailsModal}
          quotation={selectedQuotation}
          offerStatus={offerStatus}
        />
      )}
    </div>
  );
};

export default ListofQuatations;
